import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, Users, DollarSign, FolderGit2, CreditCard, Inbox } from "lucide-react";
import { useAuth, apiFetch } from "@/context/AuthContext";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";

export default function Admin() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    if (user === null) { navigate("/login"); return; }
    if (!user) return;
    apiFetch("/admin/overview").then(setData).catch(() => setErr(true));
  }, [user, navigate]);

  if (err) return <div className="min-h-screen flex items-center justify-center text-slate-400" data-testid="admin-denied">Admin access required.</div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center text-slate-400"><Loader2 className="animate-spin mr-2" /> Loading…</div>;

  const stats = [
    { icon: Users, label: "Users", value: data.users },
    { icon: DollarSign, label: "Revenue", value: `$${data.revenue}` },
    { icon: CreditCard, label: "Paid txns", value: data.paid_transactions },
    { icon: FolderGit2, label: "Projects", value: data.projects },
  ];

  return (
    <div className="min-h-screen relative">
      <div className="pointer-events-none absolute -top-40 left-0 w-[40rem] h-[40rem] bg-[#0055FF]/10 blur-[150px] rounded-full" />
      <header className="relative z-10 max-w-5xl mx-auto px-6 py-6 flex items-center justify-between">
        <span className="font-display text-xl font-bold">ArcaCentum<span className="text-[#00F0FF]">.ai</span> <span className="text-slate-500 text-sm">Admin</span></span>
        <div className="flex items-center gap-4">
          <LanguageSwitcher />
          <Link to="/admin/tickets" className="font-mono text-xs uppercase tracking-widest text-[#00F0FF] hover:text-white inline-flex items-center gap-2" data-testid="admin-tickets-link"><Inbox size={14} /> Ticket Inbox</Link>
          <Link to="/account" className="font-mono text-xs uppercase tracking-widest text-slate-400 hover:text-white inline-flex items-center gap-2"><ArrowLeft size={14} /> Account</Link>
        </div>
      </header>
      <main className="relative z-10 max-w-5xl mx-auto px-6 pb-24">
        <h1 className="font-display font-extrabold text-4xl tracking-tight">Admin Dashboard</h1>
        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="admin-stats">
          {stats.map((s) => { const Icon = s.icon; return (
            <div key={s.label} className="rounded-2xl border border-white/10 bg-[#0a0a0a] p-5">
              <div className="flex items-center gap-2 text-slate-500 mb-1"><Icon size={14} className="text-[#00F0FF]" /><span className="font-mono text-[10px] uppercase tracking-widest">{s.label}</span></div>
              <p className="font-display font-extrabold text-3xl">{s.value}</p>
            </div>
          ); })}
        </div>

        <h2 className="font-display font-bold text-xl mt-10 mb-3">Users by plan</h2>
        <div className="flex flex-wrap gap-3">
          {Object.entries(data.by_plan).map(([k, v]) => (
            <div key={k} className="rounded-xl border border-white/10 bg-[#0a0a0a] px-4 py-2 text-sm"><span className="capitalize text-slate-300">{k}</span> <span className="text-[#00F0FF] font-bold ml-2">{v}</span></div>
          ))}
        </div>

        <h2 className="font-display font-bold text-xl mt-10 mb-3">Recent users</h2>
        <div className="rounded-2xl border border-white/10 bg-[#0a0a0a] divide-y divide-white/5" data-testid="admin-users">
          {data.recent_users.map((u) => (
            <div key={u.id} className="flex items-center justify-between p-3 text-sm">
              <div><span className="text-white">{u.name}</span> <span className="text-slate-500">· {u.email}</span></div>
              <div className="flex items-center gap-3"><span className="capitalize text-slate-400">{u.plan}</span><span className="text-[#00F0FF] font-mono text-xs">{u.credits?.toLocaleString()}</span>{u.role === "admin" && <span className="text-amber-400 text-xs">admin</span>}</div>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}
